/**
 * One browser tab owns the Twilio Device. The lock is held until that tab closes
 * or hands the line to a tab that is about to dial. Other tabs wait in the lock
 * queue, so closing the leader wakes the next tab immediately. They do not poll,
 * and they do not register until they own the line.
 * Auth refresh is a separate lock (see VoiceIQAPI) so any tab may refresh while the others wait.
 */
const LEADER_LOCK = 'rp-twilio-voice-leader';
const CHANNEL_NAME = 'rp-voice-leader';
const LEASE_KEY = 'rp.voiceLeader.v1';
const HANDOFF_FRESH_MS = 10_000;
const POLL_MS = 2_000;
const YIELD_WAIT_MS = 3_000;

type VoiceLease = {
  tabId: string;
  at: number;
  hasActiveCall: boolean;
  pendingOwner?: string;
};

type LeadershipListener = (isLeader: boolean) => void;
type YieldHandler = () => boolean | Promise<boolean>;

const tabId =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;

let leader = false;
let started = false;
let holdRelease: (() => void) | null = null;
let leadershipWait: Promise<boolean> | null = null;
let channel: BroadcastChannel | null = null;
let activeCallProbe: () => boolean = () => false;
let yieldHandler: YieldHandler | null = null;
const listeners = new Set<LeadershipListener>();

function readLease(): VoiceLease | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(LEASE_KEY) || 'null') as VoiceLease | null;
    if (!parsed?.tabId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLease(lease: VoiceLease) {
  try {
    localStorage.setItem(LEASE_KEY, JSON.stringify(lease));
  } catch {
    /* ignore */
  }
}

function handoffPendingForSomeoneElse(): boolean {
  const lease = readLease();
  return !!lease?.pendingOwner && lease.pendingOwner !== tabId && Date.now() - lease.at < HANDOFF_FRESH_MS;
}

function becomeLeader() {
  if (leader) return;
  leader = true;
  listeners.forEach((listener) => listener(true));
}

function becomeFollower() {
  if (!leader) return;
  leader = false;
  listeners.forEach((listener) => listener(false));
}

function holdUntilReleased(): Promise<void> {
  return new Promise((resolve) => {
    holdRelease = resolve;
  });
}

function releaseHeldLock() {
  const release = holdRelease;
  holdRelease = null;
  release?.();
}

async function handleYield(to: string) {
  if (!leader || !to) return;
  const allow = yieldHandler ? await yieldHandler() : false;
  if (!allow) {
    channel?.postMessage({ type: 'yield-denied', to });
    return;
  }
  writeLease({ tabId: to, pendingOwner: to, at: Date.now(), hasActiveCall: false });
  if (holdRelease) {
    releaseHeldLock();
  } else {
    becomeFollower();
  }
  channel?.postMessage({ type: 'yielded', to });
}

function onChannelMessage(event: MessageEvent) {
  const data = event.data;
  if (!data || data.type !== 'yield') return;
  void handleYield(String(data.to || ''));
}

let acquireAttempt: Promise<boolean> | null = null;

function anotherTabIsInCall(): boolean {
  const lease = readLease();
  if (!lease || lease.tabId === tabId) return false;
  if (Date.now() - lease.at > 8_000) return false;
  return !!lease.hasActiveCall;
}

/** Resolves when this tab becomes leader, or immediately false if another tab still holds the lock. */
function acquireIfFree(): Promise<boolean> {
  if (leader) return Promise.resolve(true);
  if (leadershipWait || handoffPendingForSomeoneElse()) return Promise.resolve(false);
  if (acquireAttempt) return acquireAttempt;

  acquireAttempt = new Promise<boolean>((resolve) => {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks?.request) {
      const lease = readLease();
      const stale = !lease || Date.now() - lease.at > 8_000;
      if (stale || lease?.tabId === tabId || lease?.pendingOwner === tabId) {
        writeLease({ tabId, at: Date.now(), hasActiveCall: activeCallProbe() });
        becomeLeader();
        resolve(true);
        return;
      }
      resolve(false);
      return;
    }

    void locks.request(LEADER_LOCK, { ifAvailable: true }, async (lock) => {
      if (!lock || handoffPendingForSomeoneElse()) {
        resolve(false);
        return;
      }
      writeLease({ tabId, at: Date.now(), hasActiveCall: activeCallProbe() });
      becomeLeader();
      resolve(true);
      await holdUntilReleased();
      becomeFollower();
    });
  }).finally(() => {
    acquireAttempt = null;
  });

  return acquireAttempt;
}

function queueForLeadership(): Promise<boolean> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) {
    return new Promise((resolve) => {
      const lease = readLease();
      const stale = !lease || Date.now() - lease.at > 8_000;
      if (stale || lease?.pendingOwner === tabId) {
        writeLease({ tabId, at: Date.now(), hasActiveCall: false });
        becomeLeader();
        resolve(true);
        return;
      }
      if (lease?.hasActiveCall) {
        resolve(false);
        return;
      }
      const timer = window.setTimeout(() => resolve(leader), YIELD_WAIT_MS);
      const onMessage = (event: MessageEvent) => {
        if (event.data?.to !== tabId) return;
        if (event.data?.type === 'yield-denied') {
          window.clearTimeout(timer);
          channel?.removeEventListener('message', onMessage);
          resolve(false);
        }
        if (event.data?.type === 'yielded') {
          window.clearTimeout(timer);
          channel?.removeEventListener('message', onMessage);
          writeLease({ tabId, at: Date.now(), hasActiveCall: false });
          becomeLeader();
          resolve(true);
        }
      };
      channel?.addEventListener('message', onMessage);
      channel?.postMessage({ type: 'yield', to: tabId });
    });
  }

  // The standing lock queue in startLeadershipLoop is what actually takes the line.
  // This wait just asks the current owner to yield, then resolves once we hold it.
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      channel?.removeEventListener('message', onDenied);
      stopWatching();
      resolve(ok);
    };
    const stopWatching = onVoiceLeadershipChange((isLeader) => {
      if (isLeader) finish(true);
    });
    const onDenied = (event: MessageEvent) => {
      if (event.data?.type === 'yield-denied' && event.data?.to === tabId) finish(false);
    };
    channel?.addEventListener('message', onDenied);
    const timer = window.setTimeout(() => finish(leader), YIELD_WAIT_MS);
    channel?.postMessage({ type: 'yield', to: tabId });
  });
}

/**
 * Wait on the voice lock instead of polling. When the leader tab closes, the browser
 * grants this request immediately — including while this tab is in the background.
 */
function startLeadershipLoop(): boolean {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return false;

  const step = () => {
    let passedHandoff = false;
    void locks
      .request(LEADER_LOCK, async () => {
        if (handoffPendingForSomeoneElse()) {
          passedHandoff = true;
          return;
        }
        writeLease({ tabId, at: Date.now(), hasActiveCall: activeCallProbe() });
        becomeLeader();
        await holdUntilReleased();
        becomeFollower();
      })
      .finally(() => {
        // A dial handoff names another tab. Step aside briefly so that tab can take the lock.
        if (passedHandoff) {
          window.setTimeout(step, 50);
          return;
        }
        step();
      });
  };

  step();
  return true;
}

export function getVoiceTabId(): string {
  return tabId;
}

export function isVoiceTabLeader(): boolean {
  return leader;
}

export function onVoiceLeadershipChange(listener: LeadershipListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setVoiceLeaderActiveCallProbe(probe: () => boolean) {
  activeCallProbe = probe;
}

export function setVoiceLeaderYieldHandler(handler: YieldHandler) {
  yieldHandler = handler;
}

/** Fast-fail other tabs that want to dial while this tab is already on a call. */
export function noteVoiceLeaderCallActive(active: boolean) {
  if (!leader) return;
  const current = readLease();
  writeLease({
    tabId,
    at: Date.now(),
    hasActiveCall: active,
    pendingOwner: current?.pendingOwner === tabId ? undefined : current?.pendingOwner,
  });
}

export function startVoiceTabLeader() {
  if (started || typeof window === 'undefined') return;
  started = true;

  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener('message', onChannelMessage);
  }

  const waitingOnLock = startLeadershipLoop();
  if (!waitingOnLock) {
    void acquireIfFree();
  }
  window.setInterval(() => {
    if (leader) {
      writeLease({ tabId, at: Date.now(), hasActiveCall: activeCallProbe() });
      return;
    }
    // Browsers without Web Locks still discover a closed leader by polling.
    if (!waitingOnLock) void acquireIfFree();
  }, POLL_MS);

  window.addEventListener('pagehide', () => {
    if (!leader) return;
    const lease = readLease();
    if (lease?.tabId === tabId) {
      try {
        localStorage.removeItem(LEASE_KEY);
      } catch {
        /* ignore */
      }
    }
    releaseHeldLock();
  });
}

/**
 * Become the voice leader so this tab can place a call.
 * Resolves false when another tab is already on a call, or it does not hand the line over in time.
 */
export async function requestVoiceLeadership(): Promise<boolean> {
  if (leader) return true;
  if (anotherTabIsInCall()) return false;

  if (await acquireIfFree()) return true;
  if (leader) return true;
  if (anotherTabIsInCall()) return false;
  if (leadershipWait) return leadershipWait;

  const wait = queueForLeadership().finally(() => {
    if (leadershipWait === wait) leadershipWait = null;
  });
  leadershipWait = wait;
  const becameLeader = await wait;
  return becameLeader || leader;
}
